import React, { useState, useMemo, useCallback } from 'react';
import {
  Dimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { MarketplaceListing, RecentlyViewedItem } from '@/types/residents/marketplaceListing';
import { LeaveFeedbackModal } from '@/components/residents/marketplace';

import { useListingNavigation } from '@/hooks/residents/useListingNavigation';
import { useAppSelector } from '@/store';
import {
  useGetListingsQuery,
  useGetRecentlyViewedQuery,
  useClearRecentlyViewedMutation,
  useGetPendingFeedbackRequestsQuery,
  useCompleteFeedbackRequestMutation,
  FeedbackRequest,
} from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import {
  MarketplaceContent,
} from '@/components/residents/redesign';
import { MarketplaceSkeleton } from './MarketplaceSkeleton';

import { DesktopMarketplaceView } from './desktop';

type MarketplaceNavigationProp = StackNavigationProp<RootStackParamList, 'CommunityMarketplace'>;

const HP = REDESIGN_SPACING.horizontalPadding;

export const CommunityMarketplaceScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  if (layoutMode === 'desktop') return <DesktopMarketplaceView />;
  return <MobileMarketplaceView />;
};

const MobileMarketplaceView: React.FC = () => {
  const navigation = useNavigation<MarketplaceNavigationProp>();
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = user?.propertyId;
  const propertyName = user?.property?.marketingName || user?.property?.legalName || '';
  const { navigateToListing } = useListingNavigation();
  const { showSuccess, showError } = useToast();

  const [isFeedbackModalVisible, setIsFeedbackModalVisible] = useState(false);
  const [selectedFeedbackRequest, setSelectedFeedbackRequest] = useState<FeedbackRequest | null>(null);

  const {
    data: listings = [],
    isLoading: isLoadingListings,
    refetch: refetchListings,
  } = useGetListingsQuery({ propertyId });

  const {
    data: recentlyViewedListings = [],
  } = useGetRecentlyViewedQuery();

  const {
    data: pendingFeedbackRequests = [],
  } = useGetPendingFeedbackRequestsQuery();

  const [completeFeedbackRequest, { isLoading: isSubmittingFeedback }] = useCompleteFeedbackRequestMutation();
  const [clearRecentlyViewed] = useClearRecentlyViewedMutation();

  React.useEffect(() => {
    if (pendingFeedbackRequests.length > 0 && !isFeedbackModalVisible && !selectedFeedbackRequest) {
      setSelectedFeedbackRequest(pendingFeedbackRequests[0]);
      setIsFeedbackModalVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFeedbackRequests]);

  const recentlyViewed = useMemo<RecentlyViewedItem[]>(
    () =>
      recentlyViewedListings.map((listing) => ({
        id: `rv-${listing.id}`,
        image: listing.images?.[0]?.url || '',
        listing,
      })),
    [recentlyViewedListings],
  );

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetchListings();
    setRefreshing(false);
  }, [refetchListings]);

  const handlePostItem = () => navigation.navigate('PostItem', { propertyId });
  const handleMyProfile = () => navigation.navigate('MarketplaceProfile');
  const handleListingPress = (listing: MarketplaceListing) => navigateToListing(listing);
  const handleRecentlyViewedPress = (item: RecentlyViewedItem) => navigateToListing(item.listing);
  const handleClearRecentlyViewed = () => { clearRecentlyViewed(); };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalVisible(false);
    setSelectedFeedbackRequest(null);
  };

  const handleSubmitFeedback = async (rating: number, text?: string) => {
    if (!selectedFeedbackRequest) return;
    try {
      await completeFeedbackRequest({
        requestId: selectedFeedbackRequest.id,
        rating,
        text,
      }).unwrap();
      showSuccess('Your feedback has been submitted!');
      handleCloseFeedbackModal();
    } catch {
      showError('Failed to submit feedback. Please try again.');
    }
  };

  const mobileItemWidth = useMemo(() => {
    const screenWidth = Dimensions.get('window').width;
    return { width: (screenWidth - HP * 2 - REDESIGN_SPACING.gridGap) / 2 };
  }, []);

  if (isLoadingListings && !listings?.length) {
    return <MarketplaceSkeleton />;
  }

  return (
    <>
      <MarketplaceContent
        listings={listings}
        recentlyViewed={recentlyViewed}
        isLoading={isLoadingListings}
        refreshing={refreshing}
        propertyName={propertyName}
        onRefresh={handleRefresh}
        onPostItem={handlePostItem}
        onMyProfile={handleMyProfile}
        onListingPress={handleListingPress}
        onRecentlyViewedPress={handleRecentlyViewedPress}
        onClearRecentlyViewed={handleClearRecentlyViewed}
        itemStyle={mobileItemWidth}
      />

      <LeaveFeedbackModal
        visible={isFeedbackModalVisible}
        feedbackRequest={selectedFeedbackRequest}
        onClose={handleCloseFeedbackModal}
        onSubmit={handleSubmitFeedback}
        isSubmitting={isSubmittingFeedback}
      />
    </>
  );
};
